// Web tour target registry. The web tour spotlights live DOM nodes instead of
// RN refs: sidebar rows are tagged with data-tour-target in
// SidebarContent.web.tsx, the composer is found via its stable nativeID, and
// the mobile-web hamburger via the shell's class. Web-only module: never
// imported from native code paths.
import { CHAT_INPUT_NATIVE_ID } from '../assistant-ui/chatScreenSpec';

export type WebTourCaptionKey =
  | 'chat'
  | 'webcv'
  | 'jobs'
  | 'automatch'
  | 'skills'
  | 'settings';

export interface WebSidebarCaption {
  key: WebTourCaptionKey;
  selector: string;
}

// Sidebar walk order for the personal workspace. Mirrors the native
// DRAWER_CAPTIONS but matches what the web sidebar actually renders -
// analytics/shared/notifications have no web nav row, and neither does
// Connectors any more (it moved under Settings), while skills does.
export const WEB_SIDEBAR_CAPTIONS: WebSidebarCaption[] = [
  { key: 'chat', selector: '[data-tour-target="chat"]' },
  { key: 'webcv', selector: '[data-tour-target="webcv"]' },
  { key: 'jobs', selector: '[data-tour-target="jobs"]' },
  { key: 'automatch', selector: '[data-tour-target="automatch"]' },
  { key: 'skills', selector: '[data-tour-target="skills"]' },
  { key: 'settings', selector: '[data-tour-target="settings"]' },
];

/** Either drawer button: the one a page header paints in flow, or the shell's
 *  floating fallback. Exactly one of the two is visible at a time. */
export const WEB_MOBILE_MENU_SELECTOR =
  '.tabbio-inline-menu-btn, .tabbio-mobile-menu-btn';

export function isElementVisible(el: Element | null): el is HTMLElement {
  return !!el && el.getClientRects().length > 0;
}

/** The composer pill. Web chat renders the docked composer's DOM pill
 * (.tabbio-composer-shell.is-pill, composer-bar.web.tsx); the RN composer with the nativeID input is the
 * fallback in case a screen still mounts it on web. If neither exists yet -
 * ChatProvider.web swaps the whole thread for a spinner while a thread's
 * messages load, taking the pill with it - fall back to the persistent chat
 * column wrapper (tagged in ProtectedWebShell), which is in the DOM from the
 * shell's first paint. The overlay re-resolves every measure tick and tries
 * the pill first, so the spotlight starts on the column immediately and snaps
 * to the pill the moment it mounts. */
export function getComposerTargetElement(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const pill = document.querySelector('.tabbio-composer-shell.is-pill');
  if (isElementVisible(pill)) return pill;
  const input = document.getElementById(CHAT_INPUT_NATIVE_ID);
  if (input) {
    const rnPill = input.parentElement;
    if (isElementVisible(rnPill)) return rnPill;
    if (isElementVisible(input)) return input;
  }
  const column = document.querySelector('[data-tour-target="chat-column"]');
  return isElementVisible(column) ? column : null;
}

export function getSidebarCaptionElement(
  caption: WebSidebarCaption,
): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector(caption.selector);
  return isElementVisible(el) ? el : null;
}

export function getMobileMenuElement(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  // querySelector would stop at the hidden fallback whenever a header paints
  // its own button, so scan the whole match list for the visible one.
  for (const el of Array.from(
    document.querySelectorAll(WEB_MOBILE_MENU_SELECTOR),
  )) {
    if (isElementVisible(el)) return el;
  }
  return null;
}
